"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import {
  trackUpgradePromptClicked,
  trackUpgradePromptDismissed,
  trackUpgradePromptShown,
} from "@/lib/analytics";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { PLAN_DISPLAY, PLANS } from "@/lib/plans/planConfig";
import { safeTrack } from "@/lib/safeTrack";

function lockIcon() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "999px",
        border: "1px solid rgba(14, 165, 233, 0.28)",
        background: "rgba(240, 249, 255, 0.9)",
        color: "#0c4a6e",
        fontSize: "0.75rem",
        fontWeight: 800,
      }}
    >
      🔒
    </span>
  );
}

export default function UpgradePrompt({
  feature,
  triggerLabel,
  benefitStatement,
  requiredPlan = PLANS.PRO,
  onDismiss,
  variant = "inline",
}) {
  const router = useRouter();
  const plan = PLAN_DISPLAY[requiredPlan] || PLAN_DISPLAY[PLANS.PRO];
  const dismissKey = useMemo(() => `upgrade_dismissed_${feature}`, [feature]);
  const clickKey = useMemo(() => `upgrade_clicked_${feature}`, [feature]);
  const shownKey = useMemo(() => `upgrade_shown_${feature}_${variant}`, [feature, variant]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(sessionStorage.getItem(dismissKey) === "true");
  }, [dismissKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(shownKey) === "true") return;
    sessionStorage.setItem(shownKey, "true");
    trackUpgradePromptShown(feature, variant, requiredPlan);
    breadcrumbs.upgradePromptShown(feature, requiredPlan);
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.UPGRADE_PROMPT_SHOWN,
            properties: { feature, requiredPlan, variant },
          }),
          keepalive: true,
        });
      },
      "upgrade_prompt_shown",
    );
  }, [feature, variant, requiredPlan, shownKey]);

  const handleUpgradeClick = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(clickKey, "true");
    }
    trackUpgradePromptClicked(feature, variant, requiredPlan);
    breadcrumbs.upgradePromptClicked(feature, requiredPlan);
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.UPGRADE_PROMPT_CLICKED,
            properties: { feature, requiredPlan, variant },
          }),
          keepalive: true,
        });
      },
      "upgrade_prompt_clicked",
    );
    router.push(`/pricing?highlight=${requiredPlan}&from=${encodeURIComponent(feature)}`);
  };

  const handleDismiss = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(dismissKey, "true");
    }
    trackUpgradePromptDismissed(feature, variant, requiredPlan);
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.UPGRADE_PROMPT_DISMISSED,
            properties: { feature, requiredPlan, variant },
          }),
          keepalive: true,
        });
      },
      "upgrade_prompt_dismissed",
    );
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  if (variant === "tooltip") {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          zIndex: 50,
          top: "calc(100% + 0.35rem)",
          right: 0,
          width: "min(320px, 94vw)",
          padding: "0.65rem 0.75rem",
          borderRadius: "12px",
          background: "rgba(15, 23, 42, 0.96)",
          color: "#e2e8f0",
          border: "1px solid rgba(148, 163, 184, 0.3)",
          boxShadow: "0 12px 28px rgba(2, 6, 23, 0.35)",
        }}
      >
        <div style={{ display: "flex", gap: "0.55rem", alignItems: "flex-start" }}>
          {lockIcon()}
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: "0.8rem", fontWeight: 700 }}>
              {triggerLabel} is available on {plan.name}
            </p>
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.74rem", lineHeight: 1.5, color: "#cbd5e1" }}>{benefitStatement}</p>
            <p style={{ margin: "0.28rem 0 0", fontSize: "0.68rem", color: "#94a3b8" }}>
              {plan.name} starts at {plan.currency === "ZAR" ? "R" : "$"}
              {plan.price}/{plan.billingPeriod}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.35rem", marginTop: "0.55rem" }}>
          <button type="button" className="ed-btn-primary" style={{ fontSize: "0.72rem", padding: "0.35rem 0.6rem" }} onClick={handleUpgradeClick}>
            {plan.ctaLabel}
          </button>
          <button type="button" className="ed-btn-ghost" style={{ fontSize: "0.7rem", padding: "0.35rem 0.55rem" }} onClick={handleDismiss}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div
        className="ed-card-glass"
        role="status"
        aria-live="polite"
        style={{
          border: "1px solid rgba(245, 158, 11, 0.4)",
          background: "rgba(255, 247, 237, 0.92)",
          borderRadius: "14px",
          padding: "0.75rem 0.85rem",
          marginBottom: "0.7rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem" }}>
          {lockIcon()}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: "0.85rem", fontWeight: 700, color: "#92400e" }}>
              {triggerLabel} is available on {plan.name}
            </p>
            <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "#7c2d12", lineHeight: 1.45 }}>{benefitStatement}</p>
            <p style={{ margin: "0.25rem 0 0", fontSize: "0.72rem", color: "#a16207" }}>
              {plan.name} starts at {plan.currency === "ZAR" ? "R" : "$"}
              {plan.price}/{plan.billingPeriod}
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>
            <button type="button" className="ed-btn-primary" onClick={handleUpgradeClick} style={{ padding: "0.45rem 0.75rem", fontSize: "0.75rem" }}>
              {plan.ctaLabel}
            </button>
            <button type="button" className="ed-btn-ghost" onClick={handleDismiss} style={{ padding: "0.35rem 0.55rem", fontSize: "0.7rem" }}>
              Not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="ed-card-glass"
      role="status"
      aria-live="polite"
      style={{
        borderRadius: "14px",
        border: "1px solid rgba(14, 165, 233, 0.28)",
        background: "rgba(240, 249, 255, 0.92)",
        padding: "0.85rem 0.9rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem" }}>
        {lockIcon()}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "0.86rem", color: "#0c4a6e" }}>
            {triggerLabel} is available on {plan.name}
          </p>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem", color: "#334155", lineHeight: 1.45 }}>{benefitStatement}</p>
          <p style={{ margin: "0.2rem 0 0", fontSize: "0.72rem", color: "#64748b" }}>
            {plan.name} starts at {plan.currency === "ZAR" ? "R" : "$"}
            {plan.price}/{plan.billingPeriod}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.3rem" }}>
          <button type="button" className="ed-btn-primary" onClick={handleUpgradeClick} style={{ padding: "0.45rem 0.75rem", fontSize: "0.75rem" }}>
            {plan.ctaLabel}
          </button>
          <button type="button" className="ed-btn-ghost" onClick={handleDismiss} style={{ padding: "0.3rem 0.45rem", fontSize: "0.68rem" }}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
